import { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const FilterPanel = ({ filters, onFilterChange, onClearFilters, isMobile = false }) => {
  const [isExpanded, setIsExpanded] = useState(!isMobile);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setIsScrolled(true);
        setIsExpanded(false);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'plumbing', label: 'Plumbing' },
    { value: 'electrical', label: 'Electrical' },
    { value: 'carpentry', label: 'Carpentry' },
    { value: 'cleaning', label: 'Cleaning' },
    { value: 'babysitting', label: 'Babysitting' },
    { value: 'tutoring', label: 'Tutoring' },
    { value: 'driving', label: 'Driving' },
    { value: 'cooking', label: 'Cooking' },
    { value: 'laundry', label: 'Laundry' },
    { value: 'errands', label: 'Errands' },
    { value: 'tech-support', label: 'Tech Support' },
    { value: 'beauty-services', label: 'Beauty Services' }
  ];

  const locationOptions = [
    { value: 'all', label: 'All Locations' },
    { value: 'manila', label: 'Manila' },
    { value: 'quezon-city', label: 'Quezon City' },
    { value: 'caloocan', label: 'Caloocan' },
    { value: 'davao', label: 'Davao City' },
    { value: 'cebu', label: 'Cebu City' },
    { value: 'zamboanga', label: 'Zamboanga City' },
    { value: 'taguig', label: 'Taguig' },
    { value: 'antipolo', label: 'Antipolo' },
    { value: 'pasig', label: 'Pasig' },
    { value: 'cagayan-de-oro', label: 'Cagayan de Oro' }
  ];

  const datePostedOptions = [
    { value: 'all', label: 'Any Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'Past Week' },
    { value: 'month', label: 'Past Month' }
  ];

  const availabilityOptions = [
    { value: 'all', label: 'All Availability' },
    { value: 'available', label: 'Available Now' },
    { value: 'busy', label: 'Busy' },
    { value: 'limited', label: 'Limited Availability' }
  ];

  const ratingOptions = [
    { value: 'all', label: 'All Ratings' },
    { value: '4.5', label: '4.5+ Stars' },
    { value: '4.0', label: '4.0+ Stars' },
    { value: '3.5', label: '3.5+ Stars' },
    { value: '3.0', label: '3.0+ Stars' }
  ];

  const verificationOptions = [
    { value: 'all', label: 'All Levels' },
    { value: 'premium', label: 'Premium' },
    { value: 'verified', label: 'Verified' },
    { value: 'basic', label: 'Basic' }
  ];

  const handleBudgetChange = (type, value) => {
    onFilterChange({
      ...filters,
      budget: {
        ...filters?.budget,
        [type]: value
      }
    });
  };

  const FilterContent = () => (
    <div className="space-y-4 md:space-y-5">
      <div>
        <label className="text-sm font-medium text-foreground mb-2 block flex items-center gap-2">
          <Icon name="Filter" size={14} color="var(--color-primary)" />
          Quick Filters
        </label>
        <p className="text-xs text-muted-foreground mb-3">
          Use the search bar and tags above for more specific filtering
        </p>
      </div>

      <Select
        label="Category"
        options={categoryOptions}
        value={filters?.category}
        onChange={(value) => onFilterChange({ ...filters, category: value })}
        searchable
      />

      <Select
        label="Location"
        options={locationOptions}
        value={filters?.location}
        onChange={(value) => onFilterChange({ ...filters, location: value })}
        searchable
      />

      <div className="space-y-3">
        <label className="text-sm font-medium text-foreground">Budget Range (₱)</label>
        <div className="grid grid-cols-2 gap-3">
          <Input
            type="number"
            placeholder="Min"
            value={filters?.budget?.min}
            onChange={(e) => handleBudgetChange('min', e?.target?.value)}
          />
          <Input
            type="number"
            placeholder="Max"
            value={filters?.budget?.max}
            onChange={(e) => handleBudgetChange('max', e?.target?.value)}
          />
        </div>
      </div>

      <Select
        label="Date Posted"
        options={datePostedOptions}
        value={filters?.datePosted}
        onChange={(value) => onFilterChange({ ...filters, datePosted: value })}
      />

      <Select
        label="Minimum Rating"
        options={ratingOptions}
        value={filters?.minRating || 'all'}
        onChange={(value) => onFilterChange({ ...filters, minRating: value === 'all' ? null : value })}
      />

      <Select
        label="Availability"
        options={availabilityOptions}
        value={filters?.availability || 'all'}
        onChange={(value) => onFilterChange({ ...filters, availability: value })}
      />

      <Select
        label="Verification Level"
        options={verificationOptions}
        value={filters?.verificationLevel || 'all'}
        onChange={(value) => onFilterChange({ ...filters, verificationLevel: value })}
      />

      <div className="space-y-3">
        <Checkbox
          label="Verified Clients Only"
          checked={filters?.verifiedOnly}
          onChange={(e) => onFilterChange({ ...filters, verifiedOnly: e?.target?.checked })}
        />
        <Checkbox
          label="Urgent Jobs Only"
          checked={filters?.urgentOnly}
          onChange={(e) => onFilterChange({ ...filters, urgentOnly: e?.target?.checked })}
        />
      </div>

      <Button
        variant="outline"
        fullWidth
        onClick={onClearFilters}
        iconName="X"
        iconPosition="left"
      >
        Clear All Filters
      </Button>
    </div>
  );

  return (
    <div className={`bg-white border border-gray-100 rounded-xl transition-all duration-300 ${
      isExpanded ? 'shadow-sm' : 'shadow-none'
    } ${isScrolled && !isExpanded ? 'sticky top-[70px] z-10' : ''}`}>
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 md:p-4 hover:bg-gray-50 transition-colors rounded-t-xl"
      >
        <div className="flex items-center gap-2">
          <Icon name="SlidersHorizontal" size={18} color="#1E73BE" />
          <span className="text-sm md:text-base font-semibold text-foreground">
            Filters
          </span>
          {!isExpanded && (
            <span className="text-xs text-muted-foreground ml-2">
              ({Object.values(filters)?.filter(v => v && v !== 'all')?.length} active)
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isExpanded && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e?.stopPropagation();
                onClearFilters();
              }}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Clear All
            </Button>
          )}
          <Icon 
            name={isExpanded ? "ChevronUp" : "ChevronDown"} 
            size={20} 
            color="#1E73BE" 
          />
        </div>
      </button>
      {/* Expandable Content */}
      <div className={`overflow-hidden transition-all duration-300 ${
        isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className="p-3 md:p-4 pt-0">
          <FilterContent />
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;